import { Auth } from "aws-amplify";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import Projects from "../../components/Project/Projects";
import Notification from "../../components/Notification";
import ActivityNotifications from "../../components/Dashboard/ActivityNotifications";
import { ApiHelper } from "../../utils/ApiHelper";
import { useCompanyData } from "../../hooks/useCompanyData";
import { SELLER_REQUIREMENTS_COUNT_BY_STATUS, SELLER_BUDGET_DISTRIBUTION, SELLER_UPCOMING_MILESTONES, UPDATE_COMPANY, REQUIREMENTS_COUNT_BY_STATUS } from "../../lib/API_KEYS";

export default function Sellerdashboard() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const { company: sellerData, loading: companyLoading } = useCompanyData();
  const [requirementsCount, setRequirementsCount] = useState({})
  const [buyerRequirementsCount, setBuyerRequirementsCount] = useState({})
  const [budgetData, setBudgetData] = useState(null);
  const [upcomingMilestones, setUpcomingMilestones] = useState([]);
  const [acceptedDismissed, setAcceptedDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleDismissCongratulations = async () => {
    try {
      await new ApiHelper().FetchFromServer(
        UPDATE_COMPANY[0].replace(":companyId", sellerData?._id),
        UPDATE_COMPANY[1],
        undefined, { closeMessage: true })
      setAcceptedDismissed(true);
    } catch (err) {
      console.error('Error dismissing congratulations:', err);
    }
  };

  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const fullName = user.attributes.name;
        const first = fullName.split(" ")[0];
        setUsername(first);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (sellerData) {
      setLoading(false);
    }
  }, [sellerData]);

  useEffect(() => {
    const fetchRequirementsCount = async () => {
      const res = await new ApiHelper().FetchFromServer(
        ...SELLER_REQUIREMENTS_COUNT_BY_STATUS,
      );
      const data = Object.fromEntries(res.data?.map((r) => [r._id, r.count]) || [])
      setRequirementsCount(data || null);
    }
    const fetchBuyerRequirementsCount = async () => {
      const res = await new ApiHelper().FetchFromServer(
        ...REQUIREMENTS_COUNT_BY_STATUS,
      );
      const data = Object.fromEntries(res.data?.map((r) => [r._id, r.count]) || [])
      setBuyerRequirementsCount(data || null);
    }
    fetchRequirementsCount()
    fetchBuyerRequirementsCount()
  }, [])

  useEffect(() => {
    const fetchBudgetData = async () => {
      try {
        const res = await new ApiHelper().FetchFromServer(
          ...SELLER_BUDGET_DISTRIBUTION
        );
        setBudgetData(res.data);
      } catch (err) {
        console.error('Error fetching budget data:', err);
      }
    };
    fetchBudgetData();
  }, []);

  useEffect(() => {
    const fetchUpcomingMilestones = async () => {
      try {
        const res = await new ApiHelper().FetchFromServer(
          ...SELLER_UPCOMING_MILESTONES
        );
        setUpcomingMilestones(res.data || []);
      } catch (err) {
        console.error('Error fetching upcoming milestones:', err);
      }
    };
    fetchUpcomingMilestones();
  }, []);


  const renderNotification = () => {
    if (loading) return null;
    // if (acceptedDismissed) return null;

    if (!sellerData?.status || sellerData?.status === 'Pending') {
      return (
        <Notification
          variant="empty"
          heading="You need to fill out your profile information form in order to be reviewed"
          subHeading="Applications not filled out within 24 hours will be deleted automatically"
          link="/seller-dashboard/additional-info"
          text="Fill out Form"
        />
      );
    }

    switch (sellerData.status) {
      case "In Review":
        return (
          <Notification
            variant="pending"
            heading="Your application is under consideration"
            subHeading="Your application will be reviewed within 24 hours. Once admin approves or rejects, we'll notify you."
          />
        );

      case "Query Raised":
        return (
          <Notification
            variant="rejected"
            heading="Admin Raised Concerns About Your Application"
            subHeading={sellerData.concern || "Please review and update your application information."}
            link="/seller-dashboard/additional-info"
            text="Update Application Info"
            buttonStyle={{ backgroundColor: "#ffc107", color: "white" }}
          />
        );

      case "Rejected":
        return (
          <Notification
            variant="rejected"
            heading="Your application was permanently rejected"
            subHeading={sellerData.rejectionReason || "Unfortunately your application was rejected. Please contact support if you believe this was an error."}
          />
        );

      case "Approved":
        return (
          (!sellerData?.closeMessage && !acceptedDismissed) &&
          <Notification
            variant="accepted"
            heading="Congratulations! You have been reviewed and approved"
            subHeading="Now you can use our platform as a verified seller"
            onClose={handleDismissCongratulations}
          />
        );

      default:
        return null;
    }
  };

  const handleMetricClick = (metricType) => {
    const tabMapping = {
      'total': 'new',
      'shown-interest': 'interested-in-review',
      'in-review': 'interested-shortlisted',
      'won': 'interested-closed',
      'lost': 'rejected'
    };

    const targetTab = tabMapping[metricType];
    navigate('/seller-dashboard/requirements', {
      state: { activeTab: targetTab }
    });
  };

  const data = [
    { icon: "/images/total.svg", number: (Object.values(requirementsCount).reduce((s, n) => s + n, 0) + buyerRequirementsCount?.['Seller Intimation']) || 0, text: "Total", metricType: "total" },
    { icon: "/images/shown.svg", number: requirementsCount?.['Seller Interested'] || 0, text: "Shown Interest", metricType: "shown-interest" },
    { icon: "/images/review.svg", number: (requirementsCount?.['Buyer Interested', "NDA Initiated"] + requirementsCount?.["NDA Initiated"]) || 0, text: "In Review", metricType: "in-review" },
    { icon: "/images/won.svg", number: requirementsCount?.["Feedback Completed"] || 0, text: "Won", metricType: "won" },
    { icon: "/images/lost.svg", number: (requirementsCount?.["Seller Not Interested"] + requirementsCount?.["Buyer Rejected"] + requirementsCount?.["Lost"]) || 0, text: "Lost", metricType: "lost" },
  ];

  // For display: if all values are 0, show equal distribution in chart but $0 in legend
  const hasData = budgetData && budgetData.total > 0;

  const pieData = hasData ? [
    { name: "Won", value: budgetData.won, color: "#04B440", displayValue: budgetData.won },
    { name: "In Execution", value: budgetData.inExecution, color: "#056EE9", displayValue: budgetData.inExecution },
    { name: "In Review", value: budgetData.inReview, color: "#F6B100", displayValue: budgetData.inReview },
  ] : [
    { name: "Won", value: 1, color: "#04B440", displayValue: 0 },
    { name: "In Execution", value: 1, color: "#056EE9", displayValue: 0 },
    { name: "In Review", value: 1, color: "#F6B100", displayValue: 0 },
  ];
  const totalValue = budgetData ? budgetData.total : 0;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleProjectClick = (groupId) => {
    navigate(`/seller-dashboard/communication/${groupId}`);
  };

  return (
    <>
      {renderNotification()}

      <div className="container-fluid my-4">
        <div className="row align-items-center justify-content-between">
          <div className="col-6 d-flex align-items-center">
            <img
              src="/images/hand.svg"
              alt="wave"
              className="me-2"
              style={{ width: "24px", height: "24px" }}
            />
            <h4 className="mb-0">Hello, {username}</h4>
          </div>
          <div className="col-6 d-flex justify-content-end">
            <div
              className="d-inline-flex align-items-center px-3 py-2 text-nowrap flex-shrink-0"
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "50px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <img
                src="/images/calendar.svg"
                alt="Calendar"
                style={{ width: "24px", height: "24px", marginRight: "8px" }}
              />
              <span>{formattedDate}</span>
            </div>
          </div>
          <div className="col-12 mt-2">
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Your personalized dashboard for smarter control and seamless
              experience
            </p>
          </div>
        </div>
      </div>

      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 g-4 m-0 w-100">
        {data.map((item, index) => (
          <div className="col" key={index}>
            <div
              className="card shadow-sm border rounded-3 h-100"
              onClick={() => handleMetricClick(item.metricType)}
              style={{
                backgroundImage: "url('/images/background.svg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "none",
                minHeight: "160px",
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "";
              }}
            >
              <div className="card-body d-flex flex-column align-items-start justify-content-center">
                <img
                  src={item.icon}
                  alt={item.text}
                  style={{
                    width: "40px",
                    height: "40px",
                    marginBottom: "10px",
                  }}
                />
                <h4 className="fw-bold text-dark mb-1">{item.number}</h4>
                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                  {item.text}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="container-fluid my-4">
        <div className="row g-4">
          <div className="col-12 col-lg-4">
            <div className="card shadow-sm rounded-3 h-100 p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 fw-bold text-dark">Distribution by Budget</h5>
              </div>
              <div className="d-flex flex-column align-items-center gap-4">
                <div style={{ width: "100%", maxWidth: "200px", height: "150px" }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius="83%"
                        outerRadius="90%"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <text
                        x="50%"
                        y="45%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="18"
                        fontWeight="bold"
                      >
                        {totalValue}
                      </text>
                      <text
                        x="50%"
                        y="60%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="14"
                        fill="#6c757d"
                      >
                        Total
                      </text>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="ms-sm-4 w-100">
                  {pieData.map((item, i) => (
                    <div key={i} className="d-flex align-items-center mb-2">
                      <span
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          display: "inline-block",
                          marginRight: "8px",
                        }}
                      ></span>
                      <span className="text-dark fw-semibold me-2">
                        {item.name}
                      </span>
                      <span className="text-muted">${item.displayValue?.toLocaleString() || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-8">
            <div className="card shadow-sm rounded-3 h-100 p-3">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0 fw-bold text-dark">Milestones Expected</h5>
              </div>

              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                <table
                  className="table table-borderless align-middle mb-0"
                  style={{ minWidth: "700px" }}
                >
                  <thead
                    className="border-bottom"
                    style={{ position: "sticky", top: 0, background: "white", zIndex: 2 }}
                  >
                    <tr className="fw-semibold text-dark">
                      <th style={{ width: "15%" }}>Project ID</th>
                      <th style={{ width: "20%" }}>Milestone</th>
                      <th style={{ width: "30%" }}>Description</th>
                      <th className="text-end" style={{ width: "15%" }}>
                        Due Date
                      </th>
                      <th className="text-end" style={{ width: "20%" }}>
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingMilestones.length > 0 ? (
                      upcomingMilestones.map((item, index) => (
                        <tr key={index}>
                          <td
                            className="text-primary text-nowrap"
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => handleProjectClick(item.groupId)}
                          >
                            {item.projectId}
                          </td>
                          <td className="text-dark text-truncate">{item.milestoneName}</td>
                          <td className="text-muted text-truncate" title={item.description}>
                            {item.description || '-'}
                          </td>
                          <td className="text-end text-muted text-nowrap">
                            {formatDate(item.dueDate)}
                          </td>
                          <td className="text-end fw-bold text-dark text-nowrap">
                            ${item.amount?.toLocaleString() || 0}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-4">
                          No upcoming milestones
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {upcomingMilestones.length > 0 && (
                <div className="d-flex justify-content-end mt-3">
                  <button
                    className="btn btn-link text-primary text-decoration-none d-flex align-items-center gap-2"
                    onClick={() => navigate('/seller-dashboard/projects')}
                    style={{ fontSize: "0.9rem" }}
                  >
                    View All Milestones
                    <img src="/images/arrowRight.svg" alt="→" style={{ width: "16px" }} />
                  </button>
                </div>
              )}
            </div>
          </div>
          <ActivityNotifications userRole="seller" />
          <Projects userRole={'Seller'} />
        </div>
      </div>
    </>
  );
}
